"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.setTheme = exports.parseTheme = exports.rgbToHex = exports.componentToHex = exports.isDark = exports.getAvatar = exports.getBackgroundStyle = void 0;
const react_1 = __importDefault(require("react"));
const DefaultAvatar_1 = require("../DefaultAvatar");
const models_1 = require("../models");
const cssVariableNames = {
    container: {
        bg: {
            image: "--typebot-container-bg-image",
            color: "--typebot-container-bg-color",
        },
        fontFamily: "--typebot-container-font-family",
    },
    chat: {
        maxWidth: "--typebot-chat-view-max-width",
        bgColor: "--typebot-chat-view-bg-color",
        color: "--typebot-chat-view-color",
    },
    button: {
        active: {
            bgColor: "--typebot-button-active-bg-color",
            color: "--typebot-button-active-color",
        },
        inactive: {
            bgColor: "--typebot-button-inactive-bg-color",
            color: "--typebot-button-inactive-color",
        },
        border: "--typebot-button-border",
        shadow: "--typebot-button-shadow",
    },
    hostBubble: {
        bgColor: "--typebot-host-bubble-bg-color",
        color: "--typebot-host-bubble-color",
        border: "--typebot-host-bubble-border",
        shadow: "--typebot-host-bubble-shadow",
    },
    guestBubble: {
        bgColor: "--typebot-guest-bubble-bg-color",
        color: "--typebot-guest-bubble-color",
        border: "--typebot-guest-bubble-border",
        shadow: "--typebot-guest-bubble-shadow",
    },
    input: {
        bgColor: "--typebot-input-bg-color",
        color: "--typebot-input-color",
        placeholder: "--typebot-input-placeholder-color",
        border: "--typebot-input-border",
        shadow: "--typebot-input-shadow",
    },
    header: {
        bgColor: "--typebot-header-bg-color",
        color: "--typebot-header-color",
        border: "--typebot-header-border",
        shadow: "--typebot-header-shadow",
        maxWidth: "--typebot-header-max-width",
    },
};
const getBackgroundStyle = (backgroundValue) => {
    if (backgroundValue.includes("https://")) {
        return { backgroundImage: `url(${backgroundValue})` };
    }
    return { backgroundColor: backgroundValue };
};
exports.getBackgroundStyle = getBackgroundStyle;
const getAvatar = (chatbotName, showAvatar, value) => {
    if (!showAvatar) {
        return react_1.default.createElement(react_1.default.Fragment, null);
    }
    if (!value) {
        return react_1.default.createElement(DefaultAvatar_1.DefaultAvatar, { displayName: chatbotName });
    }
    if (value.includes("https")) {
        return react_1.default.createElement("img", { className: "w-full rounded-full", src: value, alt: "" });
    }
    return (react_1.default.createElement("div", { className: "w-full rounded-full text-2xl md:text-4xl text-center" }, value));
};
exports.getAvatar = getAvatar;
const isDark = (color) => {
    if (!(color.startsWith("#") || color.startsWith("rgba"))) {
        return false;
    }
    let currentColor = color;
    if (currentColor.startsWith("rgba")) {
        currentColor = (0, exports.rgbToHex)(color);
    }
    const c = currentColor.substring(1); // strip #
    const rgb = parseInt(c, 16); // convert rrggbb to decimal
    const r = (rgb >> 16) & 0xff; // extract red
    const g = (rgb >> 8) & 0xff; // extract green
    const b = (rgb >> 0) & 0xff; // extract blue
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    return luma < 70;
};
exports.isDark = isDark;
const componentToHex = (c) => {
    const hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
};
exports.componentToHex = componentToHex;
const rgbToHex = (rgba) => {
    if (rgba.startsWith("#")) {
        return rgba;
    }
    const colors = rgba.split(",");
    const r = parseInt(colors[0].replace("rgba(", ""));
    const g = parseInt(colors[1].trim());
    const b = parseInt(colors[2].trim().replace(")", ""));
    return "#" + (0, exports.componentToHex)(r) + (0, exports.componentToHex)(g) + (0, exports.componentToHex)(b);
};
exports.rgbToHex = rgbToHex;
const parseTheme = (chatbot) => {
    var _a, _b;
    return "theme" in chatbot
        ? chatbot.theme
        : {
            colors: chatbot.themeColors,
            fontFamily: chatbot.font.font,
            hostAvatarUrl: (_a = chatbot.hostAvatar) === null || _a === void 0 ? void 0 : _a.value,
            guestAvatarUrl: (_b = chatbot.guestAvatar) === null || _b === void 0 ? void 0 : _b.value,
            customHeader: chatbot.themeColors.customHeader,
        };
};
exports.parseTheme = parseTheme;
const setTheme = (theme, documentStyle, chatMaxWidth) => {
    var _a, _b;
    const { siteBackground } = theme.colors;
    documentStyle.setProperty(siteBackground.type === models_1.BackgroundType.IMAGE
        ? cssVariableNames.container.bg.image
        : cssVariableNames.container.bg.color, siteBackground.value);
    documentStyle.setProperty(cssVariableNames.container.fontFamily, theme.fontFamily);
    if (chatMaxWidth)
        documentStyle.setProperty(cssVariableNames.chat.maxWidth, `${chatMaxWidth}px`);
    documentStyle.setProperty(cssVariableNames.chat.bgColor, theme.colors.chatbotBackground.value);
    if ((0, exports.isDark)(theme.colors.chatbotBackground.value))
        documentStyle.setProperty(cssVariableNames.chat.color, "#ffffff");
    setButtonCssProps(theme.colors.buttonColor, documentStyle);
    setHostBubbleCssProps(theme.colors.hostBubble, documentStyle);
    setGuestBubbleCssProps(theme.colors.guestBubble, documentStyle);
    if (theme.colors.inputColor)
        setInputCssProps(theme.colors.inputColor, documentStyle);
    if (((_a = theme.customHeader) === null || _a === void 0 ? void 0 : _a.colors) || ((_b = theme.customHeader) === null || _b === void 0 ? void 0 : _b.maxWidth))
        setHeaderCssProps(theme.customHeader, documentStyle);
};
exports.setTheme = setTheme;
const setButtonCssProps = (buttonColors, documentStyle) => {
    documentStyle.setProperty(cssVariableNames.button.active.bgColor, buttonColors.active.backgroundColor.value);
    documentStyle.setProperty(cssVariableNames.button.active.color, buttonColors.active.textColor.value);
    documentStyle.setProperty(cssVariableNames.button.inactive.bgColor, buttonColors.inactive.backgroundColor.value);
    documentStyle.setProperty(cssVariableNames.button.inactive.color, buttonColors.inactive.textColor.value);
    if (buttonColors.active.borderColor)
        documentStyle.setProperty(cssVariableNames.button.border, `1px solid ${buttonColors.active.borderColor.value}`);
    if (buttonColors.active.shadow)
        documentStyle.setProperty(cssVariableNames.button.shadow, buttonColors.active.shadow);
};
const setHostBubbleCssProps = (colors, documentStyle) => {
    documentStyle.setProperty(cssVariableNames.hostBubble.color, colors.textColor.value);
    documentStyle.setProperty(cssVariableNames.hostBubble.bgColor, colors.backgroundColor.value);
    if (colors.borderColor)
        documentStyle.setProperty(cssVariableNames.hostBubble.border, `1px solid ${colors.borderColor.value}`);
    if (colors.shadow)
        documentStyle.setProperty(cssVariableNames.hostBubble.shadow, colors.shadow);
};
const setGuestBubbleCssProps = (colors, documentStyle) => {
    documentStyle.setProperty(cssVariableNames.guestBubble.color, colors.textColor.value);
    documentStyle.setProperty(cssVariableNames.guestBubble.bgColor, colors.backgroundColor.value);
    if (colors.borderColor)
        documentStyle.setProperty(cssVariableNames.guestBubble.border, `1px solid ${colors.borderColor.value}`);
    if (colors.shadow)
        documentStyle.setProperty(cssVariableNames.guestBubble.shadow, colors.shadow);
};
const setInputCssProps = (colors, documentStyle) => {
    var _a, _b;
    documentStyle.setProperty(cssVariableNames.input.color, colors.textColor.value);
    documentStyle.setProperty(cssVariableNames.input.bgColor, colors.backgroundColor.value);
    if (colors.borderColor)
        documentStyle.setProperty(cssVariableNames.input.border, `1px solid ${colors.borderColor.value}`);
    if (colors.shadow)
        documentStyle.setProperty(cssVariableNames.input.shadow, colors.shadow);
    if (colors.placeholder)
        documentStyle.setProperty(cssVariableNames.input.placeholder, (_b = (_a = colors.placeholder) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : (colors.backgroundColor && (0, exports.isDark)(colors.backgroundColor.value)
            ? "white"
            : "#9095a0"));
};
const setHeaderCssProps = (settings, documentStyle) => {
    documentStyle.setProperty(cssVariableNames.header.color, settings.colors.textColor.value);
    documentStyle.setProperty(cssVariableNames.header.bgColor, settings.colors.backgroundColor.value);
    if (settings.colors.borderColor)
        documentStyle.setProperty(cssVariableNames.header.border, `1px solid ${settings.colors.borderColor.value}`);
    if (settings.colors.shadow)
        documentStyle.setProperty(cssVariableNames.header.shadow, settings.colors.shadow);
    if (settings.maxWidth)
        documentStyle.setProperty(cssVariableNames.header.maxWidth, `${settings.maxWidth}px`);
};
//# sourceMappingURL=theme.js.map